import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

export type OnboardingStepKey =
  | 'identity'
  | 'service_sites'
  | 'your_team'
  | 'first_intake'
  | 'complete';

export interface SessionOrganization {
  id: string;
  legalEntityName: string;
  tradingName: string;
  registrationNumber: string;
  emailVerifiedAt: string | null;
  abn: string;
  ndisProviderNumber: string;
  primarySector: string;
  onboardingStep: OnboardingStepKey;
  onboardingCompletedAt: string | null;
}

export interface AccessibleOrganization {
  id: string;
  name: string;
}

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  postVerifyWelcomeSeenAt: string | null;
  organization: SessionOrganization;
  /**
   * Organisations where this account has guardian access (multi-org portal).
   * Empty when the API does not send the field or the user is not a multi-org guardian.
   */
  accessibleOrganizations: readonly AccessibleOrganization[];
  /**
   * Stable permission keys (e.g. `organization.onboard`, `team.invite`) the
   * backend attributes to this member. Defaults to `[]` when the /me endpoint
   * hasn't been upgraded to emit them yet so UI gates fail closed.
   */
  permissionKeys: readonly string[];
  /** Role codes the member currently holds (e.g. `org_admin`). */
  systemRoles: readonly string[];
  /**
   * Service sites this member may access. `null` = org-wide (organisation admin
   * or `sites.read_all`). Empty array = scoped but no sites assigned yet.
   */
  visibleSiteIds: readonly string[] | null;
  /** `unlinked` = site-scoped member with no staff record yet, so they see no site data. */
  siteScopeStatus: SiteScopeStatus;
  /** Org admins only: coordinators who see no site data until they get a staff record. */
  unlinkedCoordinators: readonly UnlinkedCoordinator[];
}

export type SiteScopeStatus = 'org_wide' | 'scoped' | 'unlinked';

export interface UnlinkedCoordinator {
  id: string;
  fullName: string;
  email: string;
}

function coerceStr(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function coerceStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
}

function mapAccessibleOrganizations(raw: unknown): AccessibleOrganization[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: AccessibleOrganization[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const rec = entry as Record<string, unknown>;
    const id = coerceStr(rec.id);
    const name = coerceStr(rec.name);
    if (id) {
      out.push({ id, name: name || id });
    }
  }
  return out;
}

export interface AuthState {
  user: SessionUser | null;
  setSession: (user: SessionUser) => void;
  setUser: (user: SessionUser) => void;
  logout: () => void;
}

const ONBOARDING_STEP_KEYS: readonly OnboardingStepKey[] = [
  'identity',
  'service_sites',
  'your_team',
  'first_intake',
  'complete',
];

function coerceOnboardingStep(value: unknown): OnboardingStepKey {
  if (typeof value === 'string') {
    const match = ONBOARDING_STEP_KEYS.find((key) => key === value);
    if (match) {
      return match;
    }
  }
  return 'identity';
}

function mapOrganization(raw: Record<string, unknown>): SessionOrganization {
  const verified = raw.email_verified_at;
  const completed = raw.onboarding_completed_at;
  return {
    id: coerceStr(raw.id),
    legalEntityName: coerceStr(raw.legal_entity_name),
    tradingName: coerceStr(raw.trading_name),
    registrationNumber: coerceStr(raw.registration_number),
    emailVerifiedAt: verified == null || verified === '' ? null : coerceStr(verified),
    abn: coerceStr(raw.abn),
    ndisProviderNumber: coerceStr(raw.ndis_provider_number),
    primarySector: coerceStr(raw.primary_sector),
    onboardingStep: coerceOnboardingStep(raw.onboarding_step),
    onboardingCompletedAt:
      completed == null || completed === '' ? null : coerceStr(completed),
  };
}

export function mapSessionUser(raw: Record<string, unknown>): SessionUser {
  const org = raw.organization;
  if (!org || typeof org !== 'object') {
    throw new Error('Invalid user payload: missing organization');
  }
  const seen = raw.post_verify_welcome_seen_at;
  return {
    id: coerceStr(raw.id),
    email: coerceStr(raw.email),
    fullName: coerceStr(raw.full_name),
    postVerifyWelcomeSeenAt: seen == null || seen === '' ? null : coerceStr(seen),
    organization: mapOrganization(org as Record<string, unknown>),
    accessibleOrganizations: mapAccessibleOrganizations(raw.accessible_organizations),
    permissionKeys: coerceStringArray(raw.permission_keys),
    systemRoles: coerceStringArray(raw.system_roles),
    visibleSiteIds: coerceVisibleSiteIds(raw.visible_site_ids),
    siteScopeStatus: coerceSiteScopeStatus(raw.site_scope_status, raw.visible_site_ids),
    unlinkedCoordinators: mapUnlinkedCoordinators(raw.unlinked_coordinators),
  };
}

/** Falls back to `visible_site_ids` when a session sync payload omits the status. */
function coerceSiteScopeStatus(value: unknown, visibleSiteIds: unknown): SiteScopeStatus {
  if (value === 'org_wide' || value === 'scoped' || value === 'unlinked') {
    return value;
  }
  const ids = coerceVisibleSiteIds(visibleSiteIds);
  if (ids === null) {
    return 'org_wide';
  }
  return ids.length > 0 ? 'scoped' : 'unlinked';
}

function mapUnlinkedCoordinators(value: unknown): readonly UnlinkedCoordinator[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
    .map((row) => ({
      id: coerceStr(row.id),
      fullName: coerceStr(row.full_name),
      email: coerceStr(row.email),
    }));
}

function coerceVisibleSiteIds(value: unknown): readonly string[] | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!Array.isArray(value)) {
    return null;
  }
  return coerceStringArray(value);
}

/** Fail-closed permission check. Returns false when `user` is null. */
export function userHasPermission(user: SessionUser | null, key: string): boolean {
  if (!user) {
    return false;
  }
  return user.permissionKeys.includes(key);
}

/** Organisation web-console staff (dashboard / onboarding), excluding guardians and mobile-only roles. */
const WEB_CONSOLE_STAFF_ROLES = new Set<string>(['org_admin', 'coordinator']);

/** All roles must be web-eligible for sessions in salaamah-admin (strict subset). */
// `site_manager` = delegated site management on top of Coordinator (client 6.1).
const WEB_PORTAL_ROLE_NAMES = new Set<string>(['org_admin', 'coordinator', 'guardian', 'site_manager']);

export function memberRolesAllowedForWebPortal(systemRoles: readonly string[]): boolean {
  return (
    systemRoles.length > 0 && systemRoles.every((r) => WEB_PORTAL_ROLE_NAMES.has(r))
  );
}

/** Organisation Admin role (full org settings; coordinators excluded per product RBAC). */
export function isOrganisationAdmin(user: SessionUser | null): boolean {
  if (!user) {
    return false;
  }
  return user.systemRoles.includes('org_admin');
}

/** Permanent delete of participant and staff records (Organisation Admin only, client 7.8). */
export function canPermanentlyDeleteRecords(user: SessionUser | null): boolean {
  return userHasPermission(user, 'records.delete');
}

/** Edit and archive service sites: Org Admin, or a Coordinator with delegated site management. */
export function canManageSites(user: SessionUser | null): boolean {
  return userHasPermission(user, 'sites.manage');
}

/** Bulk exports such as the archive log and incident register (Organisation Admin, client 7.8). */
export function canRunBulkExports(user: SessionUser | null): boolean {
  return userHasPermission(user, 'data.export');
}

/** Change staff roles or grant Coordinator (Organisation Admin only, client 7.4/7.8). */
export function canAssignRoles(user: SessionUser | null): boolean {
  return userHasPermission(user, 'roles.assign');
}

/** Org-wide service site visibility (`visible_site_ids` omitted or null from API). */
export function isOrgWideSiteScope(user: SessionUser | null): boolean {
  return user !== null && user.visibleSiteIds === null;
}

/** Uses main staff workspace flows (not guardian-only); mobile-only roles never sign in on web. */
export function isStaffPortalMember(user: SessionUser | null): boolean {
  if (!user) {
    return false;
  }
  return user.systemRoles.some((r) => WEB_CONSOLE_STAFF_ROLES.has(r));
}

/** Logged in only as a participant guardian (invite activation), not as org staff. */
export function isGuardianPortalOnly(user: SessionUser | null): boolean {
  if (!user) {
    return false;
  }
  return user.systemRoles.includes('guardian') && !isStaffPortalMember(user);
}

export function isEmailVerified(user: SessionUser | null): boolean {
  return Boolean(user?.organization.emailVerifiedAt);
}

export function shouldShowPostVerifyWelcome(user: SessionUser | null): boolean {
  if (!user) {
    return false;
  }
  // Organisation-owner welcome is surfaced from the dashboard; guardians who
  // activate via the portal never hit that screen — skip so layout/nav stay usable.
  if (isGuardianPortalOnly(user)) {
    return false;
  }
  return isEmailVerified(user) && user.postVerifyWelcomeSeenAt == null;
}

export function isOnboardingComplete(user: SessionUser | null): boolean {
  if (!user) {
    return false;
  }
  return (
    user.organization.onboardingStep === 'complete' &&
    user.organization.onboardingCompletedAt !== null
  );
}

/**
 * Resolve the current onboarding step so routers/gates can redirect to the
 * correct onboarding page without round-tripping through the API. Returns
 * `null` when onboarding is fully complete.
 */
export function currentOnboardingStep(user: SessionUser | null): OnboardingStepKey | null {
  if (!user) {
    return 'identity';
  }
  const step = user.organization.onboardingStep;
  if (step === 'complete') {
    return null;
  }
  return step;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        setSession: (user) => set({ user }),
        setUser: (user) => set({ user }),
        logout: () => set({ user: null }),
      }),
      {
        name: 'salaamah-admin-auth-v2',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
        }),
      },
    ),
    { name: 'auth-store' },
  ),
);
